import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  Typography,
  Button,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import IntegrationInfoScreen from "./integration-info-screen";
import VoiceSupportLightbox from "./VoiceSupportLightbox";
import RecruiterBot from "../../components/Playground/Recruiterbot/RecruiterBot";
import { usePlaygroundContext } from "../Playground";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";

interface DemoScreenProps {
  setDemoScreen: any;
}

const DemoScreen: React.FC<DemoScreenProps> = ({ setDemoScreen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const playgroundContext = usePlaygroundContext();
  if (!playgroundContext) {
    throw new Error("PlaygroundContext is not available");
  }
  const { playground, setPlayground } = playgroundContext;
  const apiResponse = playground.screeningQuestions;
  const selectedCandidate = playground.selectedCandidate;
  const [showIntegrationInfo, setShowIntegrationInfo] = useState(false);
  const [openVoiceSupportBox, setOpenVoiceSupportBox] = useState(false);
  const [openRecruiterbot, setOpenRecruiterbot] = useState(false);

  const scrollToTop = () => {
    window.scroll(0, 0);
  };

  // Trigger scrollToTop when component is loaded
  useEffect(() => {
    scrollToTop();
  }, []);

  const url_code = apiResponse[0]?.url_code;
  const onPrevious = () => {
    setDemoScreen(false);
  };

  const handlCloseAudio = () => {
    setOpenVoiceSupportBox(false);
  };

  const handleStartAudio = () => {
    setOpenVoiceSupportBox(true);
  };

  const handleStartChat = () => {
    setOpenRecruiterbot(true);
  };

  const handleCloseChat = () => {
    setOpenRecruiterbot(false);
  };

  const handleIntegration = () => {
    setPlayground((prev: any) => ({
      ...prev,
      page: 8,
    }));
    setShowIntegrationInfo(true);
  };

  if (showIntegrationInfo) {
    return <IntegrationInfoScreen handleCloseButton={undefined} />;
  }

  return (
    <Box
      sx={{
        p: 3,
        minHeight: "100vh",
        color: "white",
      }}
    >
      <Box sx={{ maxWidth: "1200px", mx: "auto", mb: 5 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: "600",
            fontSize: "24px",
            mb: 2,
            background: "linear-gradient(90deg, #538DFF 0%, #A365F3 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
            // height: isMobile ? "29px" : "auto"
          }}
        >
          Multi-channel Screening & Assessment
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          You can offer seamless AI-driven, conversational candidate evaluation,
          using VOICE, or CHAT option.
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          Putting yourself in the shoes of the candidate, click on either option
          to start the assessment and experience smooth and real-time
          conversation with in-depth assessment and intelligent question
          adaptation based on the job role.
        </Typography>

        <Grid container spacing={2}>
          {/* Voice Interview Card */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Card
              sx={{
                p: { xs: 2, md: 4 }, // Smaller padding on mobile
                textAlign: "center",
                width: { xs: "100%", md: "80%" }, // Full width on mobile
                backgroundColor: "#050D2E",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                mb: 2,
              }}
            >
              <Box sx={{ display: isMobile ? "flex" : "block" }}>
                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                  <Box
                    sx={{
                      width: 80, // Smaller size on mobile
                      height: 80,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#1C2A61",
                    }}
                  >
                    <SmartToyOutlinedIcon
                      sx={{ fontSize: 50, color: "#fff" }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    textAlign: isMobile ? "left" : "center",
                    padding: "5px",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: "white" }}
                  >
                    AI Voice Interview
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: "#94a3b8" }}>
                    Technical Screening via AI powered voice conversation
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                sx={{
                  float: isMobile ? "left" : "none", // Keeping your original position
                  textTransform: "none",
                  color: "#1e88e5",
                  bgcolor: "#fff",
                  borderRadius: "45px",
                  padding: "8px 16px", // Keeping original padding
                  fontSize: "14px",
                  fontWeight: "bold",
                  "&:hover": {
                    bgcolor: "#f5f5f5",
                    color: "#0d47a1",
                  },
                }}
                onClick={handleStartAudio}
              >
                Start Audio Interview
              </Button>
            </Card>
          </Grid>

          {/* Ai  Chat Card */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Card
              sx={{
                p: { xs: 2, md: 4 },
                textAlign: "center",
                width: { xs: "100%", md: "80%" },
                backgroundColor: "#050D2E",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                mb: 2,
              }}
            >
              <Box sx={{ display: isMobile ? "flex" : "block" }}>
                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                  <Box
                    sx={{
                      width: 80, // Adjust size for mobile
                      height: 80,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#1C2A61",
                    }}
                  >
                    <Box
                      component="div"
                      sx={{
                        width: "40px", // Smaller size for mobile
                        height: "40px",
                        backgroundImage: `url(${process.env.PUBLIC_URL}/chatIcon.svg)`,
                        backgroundSize: "contain",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    textAlign: isMobile ? "left" : "center",
                    padding: "5px",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: "white" }}
                  >
                    AI Chat Interview
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: "#94a3b8" }}>
                    Technical Screening via AI powered chat conversation
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                sx={{
                  float: isMobile ? "left" : "none",
                  textTransform: "none",
                  color: "#1e88e5",
                  bgcolor: "#fff",
                  borderRadius: "45px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  "&:hover": {
                    bgcolor: "#f5f5f5",
                    color: "#0d47a1",
                  },
                }}
                onClick={handleStartChat}
              >
                Start Chat Interview
              </Button>
            </Card>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", md: "row" }, // Reverse order on mobile
            justifyContent: "flex-start",
            gap: 2,
            mt: 5,
            maxWidth: "80%",
            ml: 4,
          }}
        >
          <Button
            variant="outlined"
            onClick={onPrevious}
            sx={{
              textTransform: "none",
              borderColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
              height: "44px",
              backgroundColor: "#7C7C7C",
              borderRadius: "45px",
              padding: "12px 20px",
              fontSize: isMobile ? "14px" : "16px",
            }}
          >
            Previous
          </Button>

          <Button
            variant="contained"
            onClick={handleIntegration}
            sx={{
              textTransform: "none",
              color: "#fff",
              bgcolor: "#007BFF",
              height: "44px",
              borderRadius: "45px",
              padding: "12px 20px",
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: "bold",
              "&:hover": {
                bgcolor: "#0052CC",
              },
            }}
          >
            View Integration Possibilities
          </Button>
        </Box>
      </Box>

      {openVoiceSupportBox && (
        <VoiceSupportLightbox
          onClose={handlCloseAudio}
          apiResponse={apiResponse}
          selectedCandidate={selectedCandidate}
        />
      )}
      {openRecruiterbot && (
        <RecruiterBot onClose={handleCloseChat} urlCode={url_code} />
      )}
    </Box>
  );
};

export default DemoScreen;
